import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Paper,
    Alert,
    Chip,
    Avatar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { Podcasts as PodcastsIcon } from '@mui/icons-material';
import DayRangePicker from '../components/DayRangePicker';
import ExplorerExportButton from './Explorer/ExplorerExportButton';
import {
    TrendChartCard,
    autoTrendGranularity,
    GRANULARITY_LABELS,
} from './Explorer/TrendChartCard';
import { SummarySection } from './Explorer/SummarySection';
import { ExplorerBrandsSection } from './Explorer/ExplorerBrandsSection';
import { calculateMedian, explorerAccentAt, formatNumber, STALE_TIME, GC_TIME, DIGITAL_PROGRAMS_QUERY, isMongoObjectIdString } from './Explorer/utils';
import { ExplorerHeader } from './Explorer/ExplorerHeader';
import {
    MarcasChipSkeleton,
    PodcastDetailProgramRowSkeleton,
} from './Explorer/ExplorerLoadingSkeletons';
import apiService from '../services/api';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { useExplorerDates } from './Explorer/useExplorerDates';
import { useAuth } from '../contexts/AuthContext';
import type { ExportConfig } from '../export';
import { entityExportReports, exportAlternatives } from './Explorer/explorerTabs';
import { PodcastDetailEpisodesTable } from './Explorer/PodcastDetailEpisodesTable';
import { ExplorerChannelFormatLinks } from './Explorer/ExplorerChannelFormatLinks';

type ProgramSortColumn = 'name' | 'contentCount' | 'totalViews' | 'viewsPerEp' | 'totalInteractions' | 'engagementRate' | 'uniquePaidBrandsCount';
type SortDirection = 'asc' | 'desc';

type PodcastTrendRow = {
    week: string;
    weekStart: string;
    episodes: number;
    views: number;
    brands: number;
    episodesDelta: number | null;
    viewsDelta: number | null;
    brandsDelta: number | null;
};

const ExplorerPodcastDetail: React.FC = () => {
    const { t } = useTranslation('explorer');
    const { podcastId } = useParams<{ podcastId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const digitalProgramsOnly = searchParams.get(DIGITAL_PROGRAMS_QUERY) === '1';

    const { getDateFilterMinStartDate, getDateFilterMaxEndDate, hasExplorerTabAccess } = useAuth();
    const { startDate, endDate, setRange, withDates } = useExplorerDates();
    const currentMinDate = getDateFilterMinStartDate();
    const currentMaxDate = getDateFilterMaxEndDate();
    const trendGranularity = useMemo(
        () => autoTrendGranularity(startDate, endDate),
        [startDate, endDate],
    );
    const trendLabels = GRANULARITY_LABELS[trendGranularity];

    const [sortColumn, setSortColumn] = useState<ProgramSortColumn>('viewsPerEp');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSort = (column: ProgramSortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const { data: podcast, isLoading: podcastLoading, error: podcastError } = useQuery({
        queryKey: ['explorer-podcast', podcastId],
        queryFn: () => apiService.getExplorerPodcastById(podcastId!),
        enabled: !!podcastId,
        staleTime: 15 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });
    const scopePodcastId = podcast?._id;
    const channelImageSrc = podcast?.imageS3Key
        ? ensureDirectS3Url(getS3Url(podcast.imageS3Key))
        : podcast?.imageUrl || undefined;

    const exportReports = entityExportReports(hasExplorerTabAccess, 'channel').join(',');
    const exportConfig = useMemo<ExportConfig | undefined>(() => {
        const reports = exportReports ? exportReports.split(',') : [];
        if (!scopePodcastId || reports.length === 0) return undefined;
        return {
            reportType: reports[0]!,
            params: {
                startDate,
                endDate,
                ...(digitalProgramsOnly ? { type: 'digital' } : {}),
            },
            contextLabels: podcast?.title ? [{ label: t('channel'), value: podcast.title }] : [],
            initialFilters: { podcastIds: [scopePodcastId] },
            alternatives: exportAlternatives(reports),
        };
    }, [scopePodcastId, podcast?.title, startDate, endDate, exportReports, t, digitalProgramsOnly]);

    const programsSortNeedsBrands = sortColumn === 'uniquePaidBrandsCount';

    const { data: programsData, isFetching: programsFetching, isPlaceholderData: programsIsPlaceholderData, error: programsError } = useQuery({
        queryKey: [
            'explorer-podcast-programs',
            scopePodcastId,
            startDate,
            endDate,
            sortColumn,
            sortDirection,
            programsSortNeedsBrands,
            digitalProgramsOnly,
        ],
        queryFn: ({ signal }) =>
            apiService.getExplorerPrograms(
                startDate,
                endDate,
                digitalProgramsOnly ? { channelType: 'digital' } : {},
                sortColumn,
                sortDirection,
                undefined,
                false,
                signal,
                scopePodcastId,
                undefined,
                500,
                undefined,
                undefined,
                programsSortNeedsBrands,
            ),
        enabled: !!scopePodcastId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const showProgramsTableSkeleton =
        programsFetching && (!programsData || programsIsPlaceholderData);

    const programIds = useMemo(
        () => (programsData?.programs ?? []).map((p) => p._id),
        [programsData?.programs],
    );

    const { data: podcastBrandCountsData, isFetching: podcastBrandCountsFetching } =
        useQuery({
            queryKey: [
                'explorer-podcast-detail-podcast-brand-count',
                scopePodcastId,
                startDate,
                endDate,
            ],
            queryFn: ({ signal }) =>
                apiService.getExplorerPodcastsBrandCounts(
                    startDate,
                    endDate,
                    [scopePodcastId!],
                    signal,
                ),
            staleTime: STALE_TIME,
            gcTime: GC_TIME,
            enabled: !!scopePodcastId,
        });

    const canalUniqueBrandsCount =
        scopePodcastId && podcastBrandCountsData?.brandCounts
            ? (podcastBrandCountsData.brandCounts[scopePodcastId] ?? 0)
            : 0;

    const { data: programsBrandCountsData, isFetching: programsBrandCountsFetching } =
        useQuery({
            queryKey: [
                'explorer-podcast-detail-brand-counts',
                scopePodcastId,
                startDate,
                endDate,
                programIds.join(','),
            ],
            queryFn: ({ signal }) =>
                apiService.getExplorerProgramsBrandCounts(
                    startDate,
                    endDate,
                    programIds,
                    signal,
                ),
            staleTime: STALE_TIME,
            gcTime: GC_TIME,
            enabled:
                !!scopePodcastId &&
                !programsSortNeedsBrands &&
                programIds.length > 0 &&
                !showProgramsTableSkeleton,
        });

    const getProgramBrandCount = useCallback(
        (program: { _id: string; uniquePaidBrandsCount?: number }) => {
            if (programsSortNeedsBrands) {
                return program.uniquePaidBrandsCount ?? 0;
            }
            return programsBrandCountsData?.brandCounts[program._id] ?? 0;
        },
        [programsSortNeedsBrands, programsBrandCountsData?.brandCounts],
    );

    const {
        data: channelBrandsData,
        isFetching: channelBrandsFetching,
        isPlaceholderData: channelBrandsIsPlaceholderData,
    } = useQuery({
        queryKey: ['explorer-podcast-brands', scopePodcastId, startDate, endDate],
        queryFn: ({ signal }) =>
            apiService.getExplorerPodcastBrands(scopePodcastId!, startDate, endDate, signal),
        enabled: !!scopePodcastId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const showChannelBrandsSkeleton =
        channelBrandsFetching && (!channelBrandsData || channelBrandsIsPlaceholderData);

    const fetchChannelBrandMentions = useCallback(
        (brandId: string) =>
            apiService.getExplorerPodcastBrandMentions(
                scopePodcastId!,
                brandId,
                startDate,
                endDate,
                'all',
            ),
        [scopePodcastId, startDate, endDate],
    );

    const { data: trendsData, isFetching: trendsFetching, isPlaceholderData: trendsIsPlaceholderData } = useQuery({
        queryKey: ['explorer-podcast-trends', scopePodcastId, startDate, endDate, trendGranularity],
        queryFn: ({ signal }) =>
            apiService.getExplorerPodcastTrends(
                scopePodcastId!,
                startDate,
                endDate,
                false,
                signal,
                trendGranularity,
            ),
        enabled: !!scopePodcastId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        placeholderData: keepPreviousData,
    });

    const { data: trendBrandMetricsData, isFetching: trendBrandMetricsFetching } =
        useQuery({
            queryKey: [
                'explorer-podcast-trends-brand-metrics',
                scopePodcastId,
                startDate,
                endDate,
                trendGranularity,
            ],
            queryFn: ({ signal }) =>
                apiService.getExplorerPodcastTrendsBrandMetrics(
                    scopePodcastId!,
                    startDate,
                    endDate,
                    signal,
                    trendGranularity,
                ),
            enabled: !!scopePodcastId && !!trendsData,
            staleTime: STALE_TIME,
            gcTime: GC_TIME,
        });

    const mergedTrendsData = useMemo((): PodcastTrendRow[] => {
        const base = trendsData?.trends ?? [];
        const brandRows = trendBrandMetricsData?.trends ?? [];
        if (brandRows.length === 0) return base;
        const brandByWeek = new Map(brandRows.map((b) => [b.weekStart, b]));
        return base.map((t) => {
            const brand = brandByWeek.get(t.weekStart);
            return brand
                ? { ...t, brands: brand.brands, brandsDelta: brand.brandsDelta }
                : t;
        });
    }, [trendsData?.trends, trendBrandMetricsData?.trends]);

    const showTrendsSkeleton =
        trendsFetching && (!trendsData || trendsIsPlaceholderData);
    const showProgramsSkeleton =
        programsFetching && (!programsData || programsIsPlaceholderData);

    const handleProgramClick = (programId: string) => {
        navigate(withDates(`/explorador/programs/${programId}`));
    };

    if (podcastError || (!podcastLoading && !podcast)) {
        return (
            <Container maxWidth="xl" sx={{ pb: 4 }}>
                <Alert severity="error">{t('loadPodcastError')}</Alert>
            </Container>
        );
    }

    const programs = programsData?.programs ?? [];
    const totalEpisodes = programs.reduce((sum, p) => sum + (p.contentCount || 0), 0);
    const totalViews = programs.reduce((sum, p) => sum + (p.totalViews || 0), 0);
    const summaryCards = [
        {
            title: t('programs'),
            value: programs.length,
        },
        {
            title: t('episodes'),
            value: totalEpisodes,
        },
        {
            title: t('viewsTotal'),
            value: formatNumber(totalViews),
        },
        {
            title: t('activeBrands'),
            value: canalUniqueBrandsCount,
            isLoading: podcastBrandCountsFetching,
        },
    ];

    const showHeaderSkeleton = podcastLoading && !podcast;

    return (
        <Container maxWidth="xl" sx={{ pb: 4 }}>
            <ExplorerHeader
                loading={showHeaderSkeleton}
                onBack={() => navigate(-1)}
                image={{
                    name: podcast?.title ?? '',
                    src: podcast
                        ? podcast.imageS3Key
                            ? ensureDirectS3Url(getS3Url(podcast.imageS3Key))
                            : podcast.imageUrl
                        : undefined,
                }}
                title={podcast?.title ?? ''}
                subtitle={podcast?.author ?? ''}
                extra={
                    podcast ? (
                        <ExplorerChannelFormatLinks
                            current="ondemand"
                            clipsId={
                                isMongoObjectIdString(podcast.channelId)
                                    ? podcast.channelId
                                    : undefined
                            }
                        />
                    ) : null
                }
            >
                <DayRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setRange}
                    minDate={currentMinDate}
                    maxDate={currentMaxDate}
                />
                <ExplorerExportButton config={exportConfig} />
            </ExplorerHeader>

            <SummarySection title={t('periodSummary')} isLoading={showProgramsSkeleton} cards={summaryCards} />

            <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ mb: 2 }}>
                    {trendLabels.title}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TrendChartCard
                        title={t('views')}
                        dataKey="views"
                        color={explorerAccentAt(0)}
                        tooltipLabel={t('views')}
                        granularityTooltip={trendLabels.tooltip}
                        data={mergedTrendsData}
                        median={
                            mergedTrendsData.length > 0
                                ? calculateMedian(
                                      mergedTrendsData
                                          .map((t) => t.views)
                                          .filter((v): v is number => typeof v === 'number'),
                                  )
                                : undefined
                        }
                        formatValue={formatNumber}
                        yAxisWidth={45}
                        isLoading={showTrendsSkeleton}
                    />
                    <TrendChartCard
                        title={t('episodes')}
                        dataKey="episodes"
                        color={explorerAccentAt(1)}
                        tooltipLabel={t('episodes')}
                        granularityTooltip={trendLabels.tooltip}
                        data={mergedTrendsData}
                        median={
                            mergedTrendsData.length > 0
                                ? calculateMedian(
                                      mergedTrendsData
                                          .map((t) => t.episodes)
                                          .filter((v): v is number => typeof v === 'number'),
                                  )
                                : undefined
                        }
                        allowDecimals={false}
                        isLoading={showTrendsSkeleton}
                    />
                    <TrendChartCard
                        title={t('activeBrands')}
                        dataKey="brands"
                        color={explorerAccentAt(2)}
                        tooltipLabel={t('brands')}
                        granularityTooltip={trendLabels.tooltip}
                        data={mergedTrendsData}
                        median={
                            mergedTrendsData.length > 0 && !trendBrandMetricsFetching
                                ? calculateMedian(
                                      mergedTrendsData
                                          .map((t) => t.brands)
                                          .filter((v): v is number => typeof v === 'number'),
                                  )
                                : undefined
                        }
                        allowDecimals={false}
                        isLoading={showTrendsSkeleton || trendBrandMetricsFetching}
                    />
                </Stack>
            </Box>

            <Box sx={{ mb: 4 }}>
                <ExplorerBrandsSection
                    brands={channelBrandsData?.brands ?? []}
                    isLoading={showChannelBrandsSkeleton}
                    isFetching={channelBrandsFetching}
                    mentionsQueryKey={[
                        'explorer-podcast-brand-mentions',
                        scopePodcastId,
                        startDate,
                        endDate,
                        'all',
                    ]}
                    fetchMentions={fetchChannelBrandMentions}
                    showProgramName
                />
            </Box>

            <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ mb: 2 }}>
                    {t('programRankingWithCount', { count: programs.length })}
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ width: 60 }}>#</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortColumn === 'name'}
                                        direction={sortColumn === 'name' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('name')}
                                    >
                                        {t('program')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'contentCount'}
                                        direction={sortColumn === 'contentCount' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('contentCount')}
                                    >
                                        {t('episodes')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'totalViews'}
                                        direction={sortColumn === 'totalViews' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('totalViews')}
                                    >
                                        {t('views')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'viewsPerEp'}
                                        direction={sortColumn === 'viewsPerEp' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('viewsPerEp')}
                                    >
                                        {t('viewsPerEp')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'totalInteractions'}
                                        direction={sortColumn === 'totalInteractions' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('totalInteractions')}
                                    >
                                        {t('interactions')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'engagementRate'}
                                        direction={sortColumn === 'engagementRate' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('engagementRate')}
                                    >
                                        {t('engagement')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortColumn === 'uniquePaidBrandsCount'}
                                        direction={sortColumn === 'uniquePaidBrandsCount' ? sortDirection : 'desc'}
                                        onClick={() => handleSort('uniquePaidBrandsCount')}
                                    >
                                        {t('brands')}
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {showProgramsTableSkeleton ? (
                                Array.from({ length: Math.max(programIds.length, 5) }).map(
                                    (_, index) => (
                                        <PodcastDetailProgramRowSkeleton
                                            key={`skeleton-${index}`}
                                        />
                                    ),
                                )
                            ) : programs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ py: 4 }}
                                        >
                                            {t('noProgramsForChannel')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                programs.map((program, index) => (
                                        <TableRow
                                            key={program._id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleProgramClick(program._id)}
                                        >
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    color="text.secondary"
                                                >
                                                    {index + 1}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    alignItems="center"
                                                >
                                                    <Avatar
                                                        src={
                                                            program.imageS3Key
                                                                ? ensureDirectS3Url(
                                                                      getS3Url(
                                                                          program.imageS3Key,
                                                                      ),
                                                                  )
                                                                : channelImageSrc
                                                        }
                                                        alt={program.name}
                                                        sx={{ width: 40, height: 40 }}
                                                    >
                                                        {channelImageSrc ? null : <PodcastsIcon />}
                                                    </Avatar>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {program.name}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                {program.contentCount}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatNumber(program.totalViews)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatNumber(program.viewsPerEp || 0)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatNumber(
                                                    program.totalInteractions || 0,
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {program.engagementRate.toFixed(2)}%
                                            </TableCell>
                                            <TableCell align="right">
                                                {programsBrandCountsFetching &&
                                                !programsSortNeedsBrands ? (
                                                    <MarcasChipSkeleton />
                                                ) : (
                                                    <Chip
                                                        label={getProgramBrandCount(program)}
                                                        size="small"
                                                        color={
                                                            getProgramBrandCount(program) > 0
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {scopePodcastId ? (
                <Box sx={{ mb: 4 }}>
                    <PodcastDetailEpisodesTable
                        podcastId={scopePodcastId}
                        startDate={startDate}
                        endDate={endDate}
                        programs={programs.map((program) => ({
                            _id: program._id,
                            name: program.name,
                        }))}
                    />
                </Box>
            ) : null}
        </Container>
    );
};

export default ExplorerPodcastDetail;
